import { Ref } from '@tblabs/truffle';

export type translations = { EN: string, PL: string, UK: string, RU: string, DE: string };

export class Translations
{
    public Lang: Ref<keyof translations> = new Ref<keyof translations>("EN");

    constructor(lang: keyof translations = "EN")
    {
        this.Lang.value = lang;
    }

    private GetTranslation(tran: translations): string
    {
        return tran[this.Lang.value];
    }

    private GetTranslationRef(tran: translations): Ref<string>
    {
        const ref = new Ref<string>(tran[this.Lang.value]);

        this.Lang.OnChange(() =>
        {
            ref.value = tran[this.Lang.value];
        });

        return ref;
    }

    public get Malfunction(): string
    {
        return this.GetTranslation({
            EN: "Camera malfunction",
            PL: "Awaria kamery",
            UK: "Помилка камери",
            RU: "Ошибка камеры",
            DE: "Kamera-Fehler"
        });
    }

    public get CameraSettingsNotAvailable(): string
    {
        return this.GetTranslation({
            EN: "Camera settings not available",
            PL: "Ustawienia kamery nie są dostępne",
            UK: "Налаштування камери недоступні",
            RU: "Настройки камеры недоступны",
            DE: "Kamera-Einstellungen nicht verfügbar"
        });
    }

    public get WatchersLimitExceeded(): string
    {
        return this.GetTranslation({
            EN: "Watchers limit exceeded",
            PL: "Limit obserwatorów przekroczony",
            UK: "Обмеження на кількість спостерігачів перевищено",
            RU: "Лимит наблюдателей превышен",
            DE: "Beobachter-Limit überschritten"
        });
    }

    public get AlreadyLoggedIn(): string
    {
        return this.GetTranslation({
            EN: "You are already logged in",
            PL: "Jesteś już zalogowany",
            UK: "Ви вже авторизовані",
            RU: "Вы уже авторизованы",
            DE: "Sie sind bereits eingeloggt"
        });
    }
    
    public get Message(): string
    {
        return this.GetTranslation({
            EN: "Message",
            PL: "Wiadomość",
            UK: "Повідомлення",
            RU: "Сообщение",
            DE: "Nachricht"
        });
    }
    
    public get CameraConnectionSound(): string 
    {
        return this.GetTranslation({
            EN: "Camera connection sound",
            PL: "Dźwięk połączenia z kamerą",
            UK: "Голос камеры",
            RU: "Звук камеры",
            DE: "Kamera-Verbindungston"
        });
    }

    public get NewPictureInLivePreviewMode(): string 
    {
        return this.GetTranslation({
            EN: "New picture in live preview mode",
            PL: "Sygnał nadejścia nowego zdjęcia w trybie podglądu na żywo",
            UK: "Сингал нового зображення в режимі прямого перегляду",
            RU: "Сигнал нового изображения в режиме прямого просмотра",
            DE: "Neues Bild im Live-Vorschau-Modus"
        });
    }
    
    public get NewPictureInSnapshotViewMode(): string 
    {
        return this.GetTranslation({
            EN: "New picture in snapshot view mode",
            PL: "Sygnał nadejścia nowego zdjęcia w trybie podglądu stopklatek",
            UK: "Сингал нового зображення в режимі перегляду снапшота",
            RU: "Сигнал нового изображения в режиме просмотра снимка",
            DE: "Neues Bild im Schnappschuss-Modus"
        });
    }

    public get SensorsSounds(): string 
    {
        return this.GetTranslation({
            EN: "Sensors sounds",
            PL: "Odgłosy sensorów dotykowych",
            UK: "Голоса сенсорів дотику",
            RU: "Звуки сенсоров дотику",
            DE: "Berührungssensor-Töne"
        });
    }

    public get Sounds(): string 
    {
        return this.GetTranslation({
            EN: "Sounds", 
            PL: "Dźwięki",
            UK: "Голоса", 
            RU: "Звуки", 
            DE: "Töne"
        });
    }

    public get SoundsInApp(): string
    {
        return this.GetTranslation({
            EN: "Sounds in app",
            PL: "Dźwięki w aplikacji",
            UK: "Голоса в додатку",
            RU: "Звуки в приложении",
            DE: "Töne in App"
        });
    }

    public get TextFromPicture(): string
    {
        return this.GetTranslation({
            EN: "Text from picture",
            PL: "Tekst ze zdjęcia",
            UK: "Текст з фото",
            RU: "Текст с фото",
            DE: "Text aus Bild"
        });
    }

    public get ConnectingToServer(): string
    {
        return this.GetTranslation({
            EN: "Connecting to server",
            PL: "Trwa łączenie z serwerem",
            UK: "З'єднання з сервером",
            RU: "Подключение к серверу",
            DE: "Verbinden mit Server"
        });
    }

    public get ConnectionInterruptedByAdmin(): string
    {
        return this.GetTranslation({
            EN: "Connection interrupted by Admin",
            PL: "Połączenie przerwane przez Administratora",
            UK: "З'єднання перерване адміністратором",
            RU: "Соединение прервано администратором",
            DE: "Verbindung unterbrochen durch Administrator"
        });
    }

    public get LoginAgain(): string 
    {
        return this.GetTranslation({
            EN: "Login again",
            PL: "Zaloguj ponownie",
            UK: "Увійти знову",
            RU: "Войти снова",
            DE: "Erneut anmelden"
        });
    }

    public get Settings(): string
    {
        return this.GetTranslation({
            EN: "Settings",
            PL: "Ustawienia",
            UK: "Налаштування",
            RU: "Настройки",
            DE: "Einstellungen"
        });
    }

    public get ApplicationSettings(): string
    {
        return this.GetTranslation({
            EN: "Application settings",
            PL: "Ustawienia aplikacji",
            UK: "Налаштування додатку",
            RU: "Настройки приложения",
            DE: "App-Einstellungen"
        });
    }

    public get AppSettings(): string
    {
        return this.GetTranslation({
            EN: "App settings",
            PL: "Ustawienia aplikacji",
            UK: "Налаштування додатку",
            RU: "Настройки приложения",
            DE: "App-Einstellungen"
        });
    }

    public get CameraSettings(): string
    {
        return this.GetTranslation({
            EN: "Camera settings",
            PL: "Ustawienia kamery",
            UK: "Налаштування камери",
            RU: "Настройки камеры",
            DE: "Kamera-Einstellungen"
        });
    }

    public get VibrationsEnabled(): string
    {
        return this.GetTranslation({
            EN: "Vibrations enabled",
            PL: "Wibracje włączone",
            UK: "Вібрації увімкнені",
            RU: "Вибрации включены",
            DE: "Vibrations aktiviert"
        });
    }

    public get VibrateOnSensorTouch(): string
    {
        return this.GetTranslation({
            EN: "Vibrate on sensor touch",
            PL: "Wibracja podczas dotykania sensorów",
            UK: "Вібрація при дотику сенсорів",
            RU: "Вибрация при дотике сенсоров",
            DE: "Berührungssensor-Vibration"
        });
    }

    public get VibrateOnStartup(): string
    {
        return this.GetTranslation({
            EN: "Vibrate on startup",
            PL: "Wibracja przy starcie",
            UK: "Вібрація при запуску",
            RU: "Вибрация при запуске",
            DE: "Start-Vibration"
        });
    }

    public get VibrateOnConnection(): string
    {
        return this.GetTranslation({
            EN: "Vibrate on connection",
            PL: "Wibracja przy połączeniu",
            UK: "Вібрація при підключенні",
            RU: "Вибрация при подключении",
            DE: "Verbindungs-Vibration"
        });
    }

    public get CameraNotConnected(): string
    {
        return this.GetTranslation({
            EN: "Camera not connected",
            PL: "Kamera nie połączona",
            UK: "Камера офлайн",
            RU: "Камера офлайн",
            DE: "Kamera offline"
        });
    }

    public get CameraConnected(): string
    {
        return this.GetTranslation({
            EN: "Camera connected",
            PL: "Kamera połączona",
            UK: "Камера онлайн",
            RU: "Камера онлайн",
            DE: "Kamera online"
        });
    }

    public get ConnectedToServer(): string
    {
        return this.GetTranslation({
            EN: "Connected to server",
            PL: "Połączono z serwerem",
            UK: "Połąчено з сервером",
            RU: "Подключено к серверу",
            DE: "Verbunden mit Server"
        });
    }

    public get ProblemWithCamera(): string
    {
        return this.GetTranslation({
            EN: "Problem with camera",
            PL: "Problem z kamerą",
            UK: "Problem z камерой",
            RU: "Problem с камерой",
            DE: "Problem mit Kamera"
        });
    }

    public get ProblemWithServer(): string
    {
        return this.GetTranslation({
            EN: "Problem with server",
            PL: "Problem z serwerem",
            UK: "Problem z сервером",
            RU: "Problem с сервером",
            DE: "Problem mit Server"
        });
    }

    public get Problem(): string
    {
        return this.GetTranslation({
            EN: "Problem",
            PL: "Problem",
            UK: "Problem",
            RU: "Problem",
            DE: "Problem"
        });
    }

    public get NoConnectionToServer(): string
    {
        return this.GetTranslation({
            EN: "No connection to server",
            PL: "Brak połączenia z serwerem",
            UK: "Немає з'єднання з сервером",
            RU: "Нет соединения с сервером",
            DE: "Keine Verbindung zum Server"
        });
    }

    public get PictureReceived(): string
    {
        return this.GetTranslation({
            EN: "Picture received",
            PL: "Odebrano zdjęcie",
            UK: "Отримано",
            RU: "Получено",
            DE: "Empfangen"
        });
    }

    public get CameraOnline(): string
    {
        return this.GetTranslation({
            EN: "Camera online",
            PL: "Kamera połączona",
            UK: "Камера онлайн",
            RU: "Камера онлайн",
            DE: "Kamera online"
        });
    }

    public get CameraOffline(): string
    {
        return this.GetTranslation({
            EN: "Camera offline",
            PL: "Kamera nie połączona",
            UK: "Камера офлайн",
            RU: "Камера офлайн",
            DE: "Kamera offline"
        });
    }

    public get HaltTransmission(): string
    {
        return this.GetTranslation({
            EN: "Halt transmission",
            PL: "Zatrzymaj nadawanie",
            UK: "Зупинити передачу",
            RU: "Остановить передачу",
            DE: "Übertragung anhalten"
        });
    }

    public get StatusLamp(): string
    {
        return this.GetTranslation({
            EN: "Status lamp",
            PL: "Światło statusu",
            UK: "Світло статусу",
            RU: "Свет статуса",
            DE: "Status-Lamp"
        });
    }

    public get InputActive(): string
    {
        return this.GetTranslation({
            EN: "Input active",
            PL: "Czujnik aktywny",
            UK: "Чувств активний",
            RU: "Чувств активный",
            DE: "Eingang aktiv"
        })
    }

    public get Meaning(): string
    {
        return this.GetTranslation({
            EN: "Meaning",
            PL: "Znaczenie",
            UK: "Значення",
            RU: "Значение",
            DE: "Bedeutung"
        });
    }

    public get CommandOnTheWay(): string
    {
        return this.GetTranslation({
            EN: "Command on the way",
            PL: "Komenda w drodze",
            UK: "Команда в дорозі",
            RU: "Команда в дорозі",
            DE: "Befehl auf dem Weg"
        });
    }

    public get Connected(): string
    {
        return this.GetTranslation({
            EN: "Connected",
            PL: "Połączono",
            UK: "Połączono",
            RU: "Połączono",
            DE: "Verbunden"
        });
    }

    public get CameraUnhalted(): string
    {
        return this.GetTranslation({
            EN: "Camera unhalted",
            PL: "Kamera uruchomiona",
            UK: "Камера запущена",
            RU: "Камера запущена",
            DE: "Kamera fortgesetzt"
        });
    }

    public get Status(): string
    {
        return this.GetTranslation({
            EN: "Status",
            PL: "Status",
            UK: "Статус",
            RU: "Статус",
            DE: "Status"
        });
    }

    public get Communication(): string
    {
        return this.GetTranslation({
            EN: "Communication",
            PL: "Komunikacja",
            UK: "Комунікація",
            RU: "Коммуникация",
            DE: "Kommunikation"
        });
    }

    public get TouchSensors(): string
    {
        return this.GetTranslation({
            EN: "Touch sensors",
            PL: "Czujniki dotykowe",
            UK: "Чувств дотику",
            RU: "Чувств дотику",
            DE: "Berührungssensoren"
        });
    }

    public get Sound(): string
    {
        return this.GetTranslation({
            EN: "Sound",
            PL: "Dźwięk",
            UK: "Голос",
            RU: "Звук",
            DE: "Ton"
        });
    }

    public get NothingHere(): string
    {
        return this.GetTranslation({
            EN: "Nothing here",
            PL: "Nic tu nie ma",
            UK: "Нічого тут",
            RU: "Ничего здесь",
            DE: "Nichts hier"
        });
    }

    public get RedirectingToLoginPage(): string
    {
        return this.GetTranslation({
            EN: "Redirecting to login page...",
            PL: "Przekierowywanie na stronę logowania...",
            UK: "Перехід на сторінку входу...",
            RU: "Переход на страницу входа...",
            DE: "Zur Anmeldeseite weiterleiten..."
        });
    }

    public get Launching(): string
    {
        return this.GetTranslation({
            EN: "Launching",
            UK: "Запуск",
            RU: "Запуск",
            DE: "Starten",
            PL: "Uruchamianie"
        });
    }

    public get PleaseStandBy(): string
    {
        return this.GetTranslation({
            EN: "Please stand by",
            UK: "Зачекайте, будь ласка",
            RU: "Пожалуйста, подождите",
            DE: "Bitte warten Sie",
            PL: "Proszę czekać"
        });
    }

    public get Width(): string
    {
        return this.GetTranslation({
            EN: "Width",
            PL: "Szerokość",
            UK: "Ширина",
            RU: "Ширина",
            DE: "Breite"
        });
    }

    public get Height(): string
    {
        return this.GetTranslation({
            EN: "Height",
            PL: "Wysokość",
            UK: "Висота",
            RU: "Высота",
            DE: "Höhe"
        });
    }

    public get Brightness(): string
    {
        return this.GetTranslation({
            EN: "Brightness",
            PL: "Jasność",
            UK: "Яскравість",
            RU: "Яркость",
            DE: "Helligkeit"
        });
    }

    public get Contrast(): string
    {
        return this.GetTranslation({
            EN: "Contrast",
            PL: "Kontrast",
            UK: "Контраст",
            RU: "Контраст",
            DE: "Kontrast"
        });
    }

    public get Rotation(): string
    {
        return this.GetTranslation({
            EN: "Rotation",
            PL: "Obrót",
            UK: "Поворот",
            RU: "Поворот",
            DE: "Rotation"
        });
    }

    public get Unauthorized(): string
    {
        return this.GetTranslation({
            EN: "Unauthorized",
            PL: "Brak autoryzacji",
            UK: "Неавторизований",
            RU: "Неавторизовано",
            DE: "Nicht autorisiert"
        });
    }

    public get CameraHalted(): string
    {
        return this.GetTranslation({
            EN: "Camera halted",
            PL: "Nadawanie zatrzymane",
            UK: "Камера зупинена",
            RU: "Камера остановлена",
            DE: "Kamera angehalten"
        });
    }

    public get Custom(): string
    {
        return this.GetTranslation({
            EN: "Custom",
            PL: "Ustawienia własne",
            UK: "Власні налаштування",
            RU: "Пользовательские",
            DE: "Benutzerdefiniert"
        });
    }

    public get CopyAddress(): string
    {
        return this.GetTranslation({
            EN: "Copy address",
            PL: "Kopiuj adres",
            UK: "Скопіювати адресу",
            RU: "Скопировать адрес",
            DE: "Adresse kopieren"
        });
    }

    public get Close(): string
    {
        return this.GetTranslation({
            EN: "Close",
            PL: "Zamknij",
            UK: "Закрити",
            RU: "Закрыть",
            DE: "Schließen"
        });
    }

    public get Copied(): string
    {
        return this.GetTranslation({
            EN: "Copied!",
            PL: "Skopiowano!",
            UK: "Скопійовано!",
            RU: "Скопировано!",
            DE: "Kopiert!"
        });
    }

    public get AutoSaveOnServer(): string
    {
        return this.GetTranslation({
            EN: "Auto save on server",
            PL: "Automatyczne zapisywanie na serwerze",
            UK: "Автоматичне збереження на сервері",
            RU: "Автоматическое сохранение на сервере",
            DE: "Automatisches Speichern auf dem Server"
        });
    }

    public get RestorePreview(): string
    {
        return this.GetTranslation({
            EN: "Restore preview",
            PL: "Przywróć podgląd",
            UK: "Відновити попередній перегляд",
            RU: "Восстановить превью",
            DE: "Vorschau wiederherstellen"
        });
    }

    public get RemoveAllSnaps(): string
    {
        return this.GetTranslation({
            EN: "Remove all snaps",
            PL: "Usuń wszystkie stopklatki",
            UK: "Видалити всі снапшоти",
            RU: "Удалить все снимки",
            DE: "Alle Schnappschüsse entfernen"
        });
    }

    public get RemoveSelected(): string
    {
        return this.GetTranslation({
            EN: "Remove selected",
            PL: "Usuń zaznaczone",
            UK: "Видалити вибране",
            RU: "Удалить выбранное",
            DE: "Ausgewähltes entfernen"
        });
    }

    public get RemoveUnselected(): string
    {
        return this.GetTranslation({
            EN: "Remove unselected",
            PL: "Usuń niezaznaczone",
            UK: "Видалити невибране",
            RU: "Удалить невыбранное",
            DE: "Nicht ausgewähltes entfernen"
        });
    }

    public get BrowserMemoryExhaustWarning(): string
    {
        return this.GetTranslation({
            EN: "⚠️ Browser memory is running low, but you can still save frames in the application memory. Saved shots will not appear after refreshing the page. Delete some pictures or save them to your computer or server by clicking the cloud icon.",
            PL: "⚠️ Kończy się pamięć przeglądarki, ale wciąż możesz zapisywać stopklatki w pamięci aplikacji. Zapisane ujęcia nie pojawią się po przeładowaniu strony. Usuń część zdjęć lub zapisz je na swoim komputerze lub serwerze klikając ikonkę chmurki",
            UK: "⚠️ Пам'ять браузера закінчується, але ви все ще можете зберігати кадри в пам'яті програми. Збережені кадри не з'являться після оновлення сторінки. Видаліть деякі фотографії або збережіть їх на свій комп'ютер або сервер, натиснувши на іконку хмарки.",
            RU: "⚠️ Заканчивается память браузера, но вы все равно можете сохранять кадры в памяти приложения. Сохраненные кадры не появятся после обновления страницы. Удалите несколько изображений или сохраните их на свой компьютер или сервер, нажав на значок облака.",
            DE: "⚠️ Der Speicher des Browsers wird knapp, aber Sie können immer noch Aufnahmen im Anwendungsspeicher speichern. Gespeicherte Aufnahmen werden nach dem Aktualisieren der Seite nicht angezeigt. Löschen Sie einige Bilder oder speichern Sie sie auf Ihrem Computer oder Server, indem Sie auf das Wolken-Symbol klicken.",
        });
    }

    public get RowSnapshotsTitle(): string
    {
        return this.GetTranslation({
            EN: "Snapshots",
            PL: "Stopklatki",
            UK: "Снапшоти",
            RU: "Снимки",
            DE: "Schnappschüsse"
        });
    }

    public get Save(): string
    {
        return this.GetTranslation({
            EN: "Save",
            PL: "Zapisz",
            UK: "Зберегти",
            RU: "Сохранить",
            DE: "Speichern"
        });
    }

    public get PicturesSaving(): string
    {
        return this.GetTranslation({
            EN: "Save Pictures",
            PL: "Zapis zdjęć",
            UK: "Зберегти зображення",
            RU: "Сохранить изображения",
            DE: "Bilder speichern",
        });
    }

    public get SaveOnServer(): string
    {
        return this.GetTranslation({
            PL: `Możesz zapisać zdjęcia na naszym serwerze klikając ikonkę chmurki w prawym dolnym rogu stopklatki`,
            EN: `You can save the pictures on our server by clicking the cloud icon in the lower right corner of the frame.`,
            UK: `Ви можете зберегти зображення на нашому сервері, натиснувши на іконку хмарки в правому нижньому куті кадру.`,
            RU: `Вы можете сохранить изображения на нашем сервере, нажав на иконку облака в правом нижнем углу кадра.`,
            DE: `Sie können die Bilder auch auf unserem Server speichern, indem Sie auf das Wolken-Symbol in der unteren rechten Ecke des Rahmens klicken.`,
        });
    }

    public get OpenSavedOnServer(): string
    {
        return this.GetTranslation({
            EN: "Open saved on the server",
            PL: "Otwórz zapisane na serwerze",
            UK: "Відкрити збережене на сервері",
            RU: "Открыть сохраненное на сервере",
            DE: "Auf dem Server gespeichert öffnen",
        });
    }

    public get MoreInfo(): string
    {
        return this.GetTranslation({
            EN: "More Info",
            PL: "Więcej informacji",
            UK: "Більше інформації",
            RU: "Больше информации",
            DE: "Weitere Informationen",
        });
    }

    public get DetailsAboutServerSaving(): string
    {
        return this.GetTranslation({
            EN: "Server Saving - Details",
            PL: "Zapis na serwerze - szczegóły",
            UK: "Збереження на сервері - Деталі",
            RU: "Сохранение на сервере - Подробности",
            DE: "Server-Speicherung - Details",
        });
    }

    public get SavedPicturesInfo(): string
    {
        return this.GetTranslation({
            PL: "Link do zapisanych zdjęć jest unikalny i nie zawiera informacji na temat dostępu do kamery. Nie ma limitu co do ilości zapisanych zdjęć.\n\nObrazy są przechowywane na serwerze tylko przez kilka dni.",
            EN: "The link to the saved pictures is unique and does not contain any information about accessing the camera. There is no limit to the number of saved pictures.\n\nImages are stored on the server for only a limited time, typically for a few days.",
            UK: "Посилання на збережені зображення є унікальним і не містить жодної інформації щодо доступу до камери. Немає обмеження щодо кількості збережених зображень.\n\nЗображення зберігаються на сервері лише обмежений час, зазвичай кілька днів.",
            RU: "Ссылка на сохраненные изображения уникальна и не содержит информации о доступе к камере. Нет ограничений по количеству сохраненных изображений.\n\nИзображения хранятся на сервере только ограниченное время, обычно несколько дней.",
            DE: "Der Link zu den gespeicherten Bildern ist eindeutig und enthält keine Informationen zum Zugriff auf die Kamera. Es gibt keine Begrenzung für die Anzahl der gespeicherten Bilder.\n\nDie Bilder werden nur für begrenzte Zeit auf dem Server gespeichert, in der Regel für einige Tage.",
        });
    }

    public get Clear(): string
    {
        return this.GetTranslation({
            EN: "Remove",
            PL: "Usuń",
            UK: "Очистити",
            RU: "Очистить",
            DE: "Löschen"
        });
    }

    public get Cancel(): string
    {
        return this.GetTranslation({
            EN: "Cancel",
            PL: "Anuluj",
            UK: "Скасувати",
            RU: "Отмена",
            DE: "Abbrechen"
        });
    }

    public get MemoryExhaust(): string
    {
        return this.GetTranslation({
            EN: "Memory exhaust",
            PL: "Koniec pamięci",
            UK: "Вичерпання пам'яті",
            RU: "Исчерпание памяти",
            DE: "Speichererschöpfung"
        });
    }

    public get MemoryCleanup(): string
    {
        return this.GetTranslation({
            EN: "Browser memory clean up",
            PL: "Czyszczenie pamięci przeglądarki",
            UK: "Очищення пам'яті",
            RU: "Очистка памяти",
            DE: "Speicherbereinigung"
        });
    }

    public get LivePreview(): string
    {
        return this.GetTranslation({
            EN: "Live preview",
            PL: "Podgląd na żywo",
            UK: "Пряма перегляд",
            RU: "Прямой просмотр",
            DE: "Live-Vorschau"
        });
    }

    public get SnapshotView(): string
    {
        return this.GetTranslation({
            EN: "Snapshot view",
            PL: "Widok stopklatki",
            UK: "Перегляд снапшота",
            RU: "Просмотр снимка",
            DE: "Schnappschuss anzeigen"
        });
    }

    public get ReceiverPageTitle(): string
    {
        return this.GetTranslation({
            EN: "Receiver",
            PL: "Odbiornik",
            UK: "Приймач",
            RU: "Приемник",
            DE: "Empfänger"
        });
    }

    public get Logout(): string
    {
        return this.GetTranslation({
            EN: "Logout",
            PL: "Wyloguj",
            UK: "Вийти",
            RU: "Выйти",
            DE: "Abmelden"
        });
    }

    public get NoPasswordProvided(): Ref<string>
    {
        return this.GetTranslationRef({
            EN: "No password provided",
            PL: "Wpisz hasło",
            UK: "Не вказано пароль",
            RU: "Пароль не указан",
            DE: "Kein Passwort angegeben"
        });
    }

    public get LoginPageTitle(): Ref<string>
    {
        return this.GetTranslationRef({
            EN: "Login",
            PL: "Logowanie",
            UK: "Вхід",
            RU: "Вход",
            DE: "Anmeldung"
        });
    }

    public get LoginButton(): Ref<string>
    {
        return this.GetTranslationRef({
            EN: "Login",
            PL: "Zaloguj",
            UK: "Увійти",
            RU: "Войти",
            DE: "Anmelden"
        });
    }

    public get Help(): Ref<string>
    {
        return this.GetTranslationRef({
            EN: "Help",
            PL: "Pomoc",
            UK: "Довідка",
            RU: "Помощь",
            DE: "Hilfe"
        });
    }

    public get Password(): Ref<string>
    {
        return this.GetTranslationRef({
            EN: "Password",
            PL: "Hasło",
            UK: "Пароль",
            RU: "Пароль",
            DE: "Passwort"
        });
    }

    public get CharactersSizeAreNotImportant(): Ref<string>
    {
        return this.GetTranslationRef({
            EN: "Characters size or spaces are not important here",
            PL: "Wielkość znaków i spacje nie mają znaczenia",
            UK: "Розмір символів або пробіли тут не мають значення",
            RU: "Размер символов или пробелы не важны здесь",
            DE: "Zeichengröße oder Leerzeichen sind hier nicht wichtig"
        });
    }

    public get InvalidPassword(): string
    {
        return this.GetTranslation({
            EN: "Invalid password",
            PL: "Nieprawidłowe hasło",
            UK: "Неправильний пароль",
            RU: "Неправильный пароль",
            DE: "Ungültiges Passwort"
        });
    }

    public get NetworkError(): string
    {
        return this.GetTranslation({
            EN: "Network error",
            PL: "Błąd sieci",
            UK: "Помилка мережі",
            RU: "Ошибка сети",
            DE: "Netzwerkfehler"
        });
    }
}
